import React, { useState } from "react";

import { apiCall } from '@app/utils/apiCall';
import { useAuth } from '@app/contexts/AuthContext';

import InputDate from "@app/components/2_widgets/inputs/InputDate";
import InputText from "@app/components/2_widgets/inputs/InputText";
import Badge from "@app/components/1_elements/Badge";
import ButtonSecondary from "@app/components/1_elements/ButtonSecondary";
import moment from 'moment';
import 'moment/locale/fr'; 
import { buildCspeRecordFormDataBuilder } from "@app/utils/form_data_builders/CspeRecordFormDataBuilder";
import { CircleCheck, CircleX, Scissors, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function CspeMonthDetail({cspeRecordbyMonth, reload, structureDataId}) {
    const auth = useAuth();
    const { t } = useTranslation();

    moment.locale('fr');
    
    const formatMonthYear = (monthYear) => {
        const [month, year] = monthYear.split('-').map(Number);
        const date = moment().set({ year, month: month - 1, date: 1 });
        const formattedDate = date.format('MMMM YYYY');

        const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

        return capitalizedDate;
    };
    
    const formattedDate = formatMonthYear(cspeRecordbyMonth.month);
    
    function handleChange(key, v, index) {
        let cspeRecord = cspeRecordbyMonth.records[index];

        // Find the CSPE record
        cspeRecord[key] = v;

        // Update the CSPE records
        updateCspeRecord(cspeRecord);
    }

    function updateCspeRecord(cspeRecord) {
        const method = cspeRecord.id ? "patch" : "post";
        const path = cspeRecord.id ? `/api/v1/cspe_records/${cspeRecord.id}` : "/api/v1/cspe_records";

        const formData = buildCspeRecordFormDataBuilder(cspeRecord)

        apiCall({
            method: method,
            path: path,
            auth: auth,
            data: formData,
            onSuccess: (response) => {
                // Reload the CSPE list
                reload();
            }
        });        
    }

    function destroyCspe(cspeRecord) {        
        apiCall({
            method: "delete",
            path: `/api/v1/cspe_records/${cspeRecord.id}`,            
            auth: auth,
            onSuccess: (response) => {
                // Reload the CSPE list
                reload();
            }
        });
    }   

    function addAndSplitCspeRecord() {
        const existingRecords = cspeRecordbyMonth.records;        

        // Find most recent cspe record
        const mostRecentRecord = existingRecords.reduce((mostRecent, record) =>
            new Date(record.end_date) > new Date(mostRecent.end_date) ? record : mostRecent
        , existingRecords[0]);
        
        // Define start_date and end_date
        const startDate = moment(mostRecentRecord.start_date);
        const endDate = moment(mostRecentRecord.end_date);

        // Calculate number of days between the start and end date
        const diffDays = endDate.diff(startDate, "days");
        const subDays = Math.floor(diffDays / 2); // Use to change end_date

        // Change new end date
        const newStartDate = endDate.subtract(subDays - 1, "days").format("YYYY-MM-DD");

        // update most recent record with new end date by substracting days
        const endDate2 = moment(mostRecentRecord.end_date);
        const newEndDate = endDate2.subtract(subDays, "days").format("YYYY-MM-DD");

        const newRecord = {
            structure_data_id: structureDataId,
            start_date: mostRecentRecord.start_date,
            end_date: newEndDate
        }        

        mostRecentRecord.start_date = newStartDate;

        // Update CSPE record in database
        updateCspeRecord(mostRecentRecord)
        // Update CSPE record in database
        updateCspeRecord(newRecord);

    } 

    function displayStatus(cspeRecord) {
        const badgeLabel = t(`cspe_record.status.${cspeRecord.status}`)
        const badgeIcon = cspeRecord.status == "nok" ? <CircleX /> : <CircleCheck />;

        return (
            <Badge icon={badgeIcon} label={badgeLabel} classProp={`badge-${cspeRecord.status}`} />
        )
    }
        
    function displayCspeRow(cspeRecord, index) {
        return (
            <div key={index} className="row align-items-center custom-row empty p-2">
                <div className="col-2">
                    <InputDate
                        inputId="starting-date"                
                        defaultValue={cspeRecord.start_date}
                        callbackOnChange={(v) => handleChange("start_date", v, index)}
                    />
                </div>
                <div className="col-1 text-center">→</div>
                <div className="col-2">
                    <InputDate 
                        inputId="ending-date"                
                        defaultValue={cspeRecord.end_date}
                        callbackOnChange={(v) => handleChange("end_date", v, index)}
                    />
                </div>
                <div className="col-1 text-center">{cspeRecord.cspe_tax_value}</div>
                <div className="col-1">
                    <InputText 
                        placeholder="0"
                        defaultValue={cspeRecord.value}
                        callbackBlur={(error, v) => handleChange("value", v, index)}
                    />
                </div>                
                <div className="col-1 text-center">
                    {displayStatus(cspeRecord)}
                </div>
                <div className="col-1">
                    <div
                        className="default-icon red" 
                        onClick={() => destroyCspe(cspeRecord)}>
                            <Trash2 className="clickable" />
                    </div>
                </div>                
            </div>
        )
    }

    return (
        <div className="custom-row p-2 mb-3">
            <p className="mb-2"><b>{formattedDate}</b></p>
            {cspeRecordbyMonth.records.map(displayCspeRow)}
            <ButtonSecondary 
                label="Ajouter une sous période" 
                onClick={() => addAndSplitCspeRecord()}
                icon={<Scissors />} 
            />
        </div>
    )
}